import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { gridConfig } from './grid-config';

const breakpoints = Object.keys(gridConfig);

const propTypes = {
	...Object.fromEntries(breakpoints.map((b) => [b, Number])),
	...Object.fromEntries(breakpoints.map((b) => [`colStart-${b}`, Number])),
	dataTestId: PropTypes.string,
};

const defaultProps = {
	...Object.fromEntries(breakpoints.map((b) => [b, null])),
	...Object.fromEntries(breakpoints.map((b) => [`colStart-${b}`, null])),
	dataTestId: null,
};

/*
 * Return the CSS that contains only the relevant breakpoints and column sizes for the grid item.
 * A column size is always defined for xxs even if not provided.
 */
const spanCols = (props) =>
	breakpoints
		.filter((breakpoint) => (props[breakpoint] || breakpoint === 'xxs') && props[breakpoint] > 0)
		.map((breakpoint) =>
			breakpoint === 'xxs'
				? `
					grid-column: auto / span ${props[breakpoint] ?? 12};
					display: grid;
				`
				: `@media screen and (min-width: ${gridConfig[breakpoint].minWidth}) {
					grid-column: auto / span ${props[breakpoint]};
					display: grid;
				}`,
		)
		.join('');

/*
 * Return the CSS that contains only the relevant grid-column-start values for the grid item.
 * Note: The naming of the colStart prop conforms to colStart-[breakpoint].
 */
const colStart = (props) =>
	breakpoints
		.filter((breakpoint) => props[`colStart-${breakpoint}`])
		.map((breakpoint) => {
			const colStartValue = props[`colStart-${breakpoint}`];
			return breakpoint === 'xxs'
				? `grid-column-start: ${colStartValue};`
				: `@media screen and (min-width: ${gridConfig[breakpoint].minWidth}) {
					grid-column-start: ${colStartValue};
				}`;
		})
		.join('');

const StyledComponent = styled.div.withConfig({
	shouldForwardProp: (prop) => ![
		'xxs',
		'colStart-xxs',
		'xs',
		'colStart-xs',
		's',
		'colStart-s',
		'm',
		'colStart-m',
		'l',
		'colStart-l',
		'xl',
		'colStart-xl',
		'xxl',
		'colStart-xxl',
	].includes(prop),
})`
	grid-column: auto / span 12;
	display: none;
	
	${(props) => spanCols(props)}
	${(props) => colStart(props)}
`;

const GridItem = React.forwardRef(({ dataTestId, ...props }, ref) => (
	<StyledComponent
		data-testid={dataTestId}
		ref={ref}
		className="fuse-responsive-grid__item"
		{...props}
	/>
));

GridItem.propTypes = propTypes;
GridItem.defaultProps = defaultProps;
GridItem.displayName = 'GridItem';

export default GridItem;
